import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AddReleaseCommentReplyRequest,
  CreateReleaseCommentRequest,
  EsraPipingAPIClient,
  ReleaseCommentViewModel,
  UpdateReleaseCommentRequest,
  UpdateReleaseCommentResolutionStatusRequest,
  CommentResolutionStatus,
} from '../../shared/models/autogenerated-piping';

/**
 * Thin wrapper around the `EsraPipingAPIClient` release-comments endpoints.
 * Kept as a dedicated service (instead of reusing `CommentsService`, which targets
 * pipe class comments) because release comments are a single-thread-per-release
 * model with a different set of request/response shapes.
 */
@Injectable({
  providedIn: 'root',
})
export class ReleaseCommentsService {
  constructor(private readonly esraApiClient: EsraPipingAPIClient) {}

  /**
   * Gets the nested tree of comments (root comments with their replies) for a release.
   * @param releaseId The release to load comments for.
   * @returns Observable of the root comments, each with its `replies` populated.
   */
  getComments(releaseId: string): Observable<ReleaseCommentViewModel[]> {
    return this.esraApiClient.getComments2(releaseId);
  }

  /**
   * Gets the total number of comments (roots + replies) for a release.
   * @param releaseId The release to count comments for.
   * @returns Observable of the total comment count.
   */
  getCommentCount(releaseId: string): Observable<number> {
    return this.esraApiClient.getCommentCount2(releaseId);
  }

  /**
   * Creates a new root comment on a release. The BE resolves `authorId` from the JWT.
   * @param releaseId The release to comment on.
   * @param content The comment body.
   * @param authorName Display name shown for the comment author.
   * @returns Observable of the created comment.
   */
  createComment(releaseId: string, content: string, authorName: string): Observable<ReleaseCommentViewModel> {
    const body: CreateReleaseCommentRequest = new CreateReleaseCommentRequest();
    body.releaseId = releaseId;
    body.content = content;
    body.authorName = authorName;
    body.displayOrder = 0;
    return this.esraApiClient.createComment2(body);
  }

  /**
   * Adds a reply to a root comment. Replying to a reply is rejected by the BE.
   * @param parentCommentId The root comment to reply to.
   * @param content The reply body.
   * @param authorName Display name shown for the reply author.
   * @returns Observable of the created reply.
   */
  addReply(parentCommentId: string, content: string, authorName: string): Observable<ReleaseCommentViewModel> {
    const body: AddReleaseCommentReplyRequest = new AddReleaseCommentReplyRequest();
    body.content = content;
    body.authorName = authorName;
    return this.esraApiClient.addReply2(parentCommentId, body);
  }

  /**
   * Updates the content of a comment. Only the comment author is allowed (enforced by BE).
   * @param commentId The comment to update.
   * @param content The new comment body.
   * @param resolutionStatus The current resolution status, resent so the BE does not reset it.
   * @returns Observable of the updated comment.
   */
  updateComment(commentId: string, content: string, resolutionStatus: CommentResolutionStatus): Observable<ReleaseCommentViewModel> {
    const body: UpdateReleaseCommentRequest = new UpdateReleaseCommentRequest();
    body.id = commentId;
    body.content = content;
    body.resolutionStatus = resolutionStatus;
    return this.esraApiClient.updateComment2(commentId, body);
  }

  /**
   * Deletes a comment. Only the author may delete, and only when it has no replies (enforced by BE).
   * @param commentId The comment to delete.
   * @returns Observable of the deleted comment.
   */
  deleteComment(commentId: string): Observable<ReleaseCommentViewModel> {
    return this.esraApiClient.deleteComment2(commentId);
  }

  /**
   * Updates the resolution status of a comment. Any release participant may call this.
   * @param commentId The comment to update.
   * @param resolutionStatus The new resolution status.
   * @returns Observable of the updated comment.
   */
  updateResolutionStatus(commentId: string, resolutionStatus: CommentResolutionStatus): Observable<ReleaseCommentViewModel> {
    const body: UpdateReleaseCommentResolutionStatusRequest = new UpdateReleaseCommentResolutionStatusRequest();
    body.resolutionStatus = resolutionStatus;
    return this.esraApiClient.updateResolutionStatus(commentId, body);
  }
}
